import { FC, useEffect, useMemo, useState } from "react";
import { Text, Stack, HStack } from "@chakra-ui/react";

import {
  isToday,
  isStringWithNumber,
  formatPriceToTwoDecimals,
  formatPriceWithoutZerosAndCurrency,
} from "../../../../utils";
import { PaymentBlockCard } from "./PaymentBlockCard";
import { PaymentBlockInput } from "./PaymentBlockInput";
import { PaymentBlockOptions } from "./PaymentBlockOptions";
import { PaymentBlockPaymentButton } from "./PaymentBlockPaymentButton";
import { PaymentBlockPaymentDestination } from "./PaymentBlockPaymentDestination";
import { PaymentBlockOperationInProgress } from "./PaymentBlockOperationInProgress";
import { IDebtState } from "../../../../api/debt/types";
import { IScheduleItem } from "../../../../api/schedule/types";
import { PaymentDestinationType } from "../../../../api/payments/types";

interface Props {
  nearestPayment: IScheduleItem | undefined;
  debt: IDebtState | undefined;
}

export const PaymentsSchedulePayment: FC<Props> = ({ nearestPayment, debt }) => {
  const [isManualAmount, setIsManualAmount] = useState<boolean>(false);
  const [amountFromInput, setAmountFromInput] = useState<string>("");
  const [amountFromOptions, setAmountFromOptions] = useState<string>("");
  const [paymentDestination, setPaymentDestination] = useState<PaymentDestinationType>(undefined);

  const amountForSending = useMemo(() => {
    const result = isManualAmount ? formatPriceToTwoDecimals(amountFromInput) : amountFromOptions;
    return isStringWithNumber(result) && result !== "0.00" ? result : "";
  }, [isManualAmount, amountFromInput, amountFromOptions]);

  const hasOverdue = isStringWithNumber(debt?.overdue?.total) && Number(debt?.overdue?.total) > 0;
  const isNearestToday = isToday(nearestPayment?.date || "");

  useEffect(() => {
    const fullDebtAmount = isStringWithNumber(debt?.total) ? Number(debt?.total) : 0;
    const currentAmount = isStringWithNumber(amountForSending) ? Number(amountForSending) : 0;
    const nearestAmount = isStringWithNumber(nearestPayment?.total) ? Number(nearestPayment?.total) : 0;
    const overdueAmount = isStringWithNumber(debt?.overdue?.total) ? Number(debt?.overdue?.total) : 0;

    if (fullDebtAmount > 0 && currentAmount === fullDebtAmount) {
      setPaymentDestination("EARLY_REPAYMENT");
      return;
    }

    const shouldReset
        = (fullDebtAmount > 0 && currentAmount > fullDebtAmount)
        || (isNearestToday && !hasOverdue && !!currentAmount && !!nearestAmount && currentAmount <= nearestAmount)
        || (hasOverdue && !isNearestToday && !!currentAmount && currentAmount <= overdueAmount)
        || (hasOverdue && isNearestToday && !!currentAmount && !!nearestAmount && currentAmount <= overdueAmount + nearestAmount);

    if (shouldReset) {
      setPaymentDestination(undefined);
    }
  }, [debt?.total, amountForSending, isNearestToday, nearestPayment?.total, hasOverdue]);

  const onSelectOption = (newAmount: string) => {
    setIsManualAmount(false);
    setAmountFromOptions(newAmount);

    const formattedAmount = isStringWithNumber(newAmount)
      ? formatPriceWithoutZerosAndCurrency(Number(newAmount))
      : ""; // Deselecting an option and setting empty input

    setAmountFromInput(formattedAmount);
  };

  const onChangeInput = (newAmount: string) => {
    setIsManualAmount(true);
    setAmountFromInput(newAmount);
  };

  return (
    <Stack p="20px" borderRadius="16px" bg="white" spacing={0}>
      <Text lineHeight="24px" mb="12px">Сумма платежа</Text>

      <HStack mb="7px" spacing={1}>
        <PaymentBlockInput amountFromInput={amountFromInput} onChangeInput={onChangeInput} />
        {/* <PaymentBlockCalculateButton amount={amountForSending} /> #Feature on hold */}
      </HStack>

      <PaymentBlockOptions
        nearestPayment={nearestPayment}
        debt={debt}
        onSelectOption={onSelectOption}
        isManualAmount={isManualAmount}
      />
      <PaymentBlockCard />
      <PaymentBlockPaymentDestination
        currentAmount={amountForSending}
        nearestPaymentAmount={nearestPayment?.total}
        destination={paymentDestination}
        isNearestToday={isNearestToday}
        onChange={setPaymentDestination}
      />

      <PaymentBlockOperationInProgress />
      <PaymentBlockPaymentButton
        currentAmount={amountForSending}
        nearestPaymentAmount={nearestPayment?.total}
        paymentDestination={paymentDestination}
        isNearestToday={isNearestToday}
        clearInput={() => { onSelectOption(""); }}
        resetPaymentDestination={() => { setPaymentDestination(undefined); }}
      />
    </Stack>
  );
};
